import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx, useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { productImage, onImageError } from '../utils/helpers';

export default function CartPage() {
  const { items, removeItem, updateQuantity, total, retailTotal, retailUnit } = useCartCtx();
  const { isMember } = useAuthCtx();
  const { fmt } = useCurrency();

  // Members pay VIP prices + ship free; everyone else pays retail.
  const unitPrice = (i) => (isMember ? i.priceInCents : retailUnit(i));
  const subtotal  = isMember ? total : retailTotal;
  const shipping  = isMember ? 0 : (subtotal >= 20000 ? 0 : 699); // VIP: always free; else free over $200
  const vipSavings = retailTotal - total;                         // what a member saves on this cart
  const qualifiesForGift = subtotal >= 30000; // Free gift over $300

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main id="main-content" tabIndex={-1} className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full">
        <h1 className="text-3xl font-bold mb-8">Your Cart</h1>

        {items.length === 0 ? (
          <div className="card text-center py-20">
            <p className="text-gray-500 mb-6">Your cart is empty.</p>
            <Link to="/shop" className="btn-primary">Browse Peptides</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Items list */}
            <div className="md:col-span-2 space-y-4">
              {items.map(item => (
                <div key={item.variantId} className="card flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  {/* Top row on mobile: image + info */}
                  <div className="flex items-center gap-3 sm:contents">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-tea-50 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img
                        src={productImage(item.productSlug) || item.imageUrl}
                        alt={item.productName}
                        className="w-full h-full object-cover"
                        onError={onImageError}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{item.productName}</p>
                      <p className="text-sm text-gray-500">{item.variantName}</p>
                      <p className="text-tea-700 font-bold flex items-baseline gap-1.5">
                        {fmt(unitPrice(item))}
                        {isMember && retailUnit(item) > item.priceInCents && (
                          <span className="text-xs text-gray-400 line-through font-normal">{fmt(retailUnit(item))}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  {/* Bottom row on mobile: qty + total + remove */}
                  <div className="flex items-center gap-3 sm:gap-4 sm:ml-auto">
                    <div className="flex items-center border border-gray-200 rounded-lg">
                      <button onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                        className="px-3 py-2 text-gray-500 hover:text-tea-700">−</button>
                      <span className="px-3 py-2 font-medium">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                        className="px-3 py-2 text-gray-500 hover:text-tea-700">+</button>
                    </div>
                    <p className="font-bold w-16 sm:w-20 text-right">{fmt(unitPrice(item) * item.quantity)}</p>
                    <button onClick={() => removeItem(item.variantId)}
                      className="text-red-400 hover:text-red-600 transition-colors p-1" aria-label="Remove item">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              {qualifiesForGift && (
                <div className="relative overflow-hidden rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-yellow-50 to-tea-50 p-4 flex items-center gap-4 shadow-sm">
                  <div className="absolute top-0 right-0 bg-gradient-to-br from-amber-400 to-amber-600 text-white text-[10px] font-bold tracking-wider px-3 py-1 rounded-bl-lg rounded-tr-xl shadow">
                    GIFT
                  </div>
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <svg viewBox="0 0 64 64" className="w-14 h-14" aria-hidden="true">
                      <defs>
                        <linearGradient id="giftBox" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#b45309" />
                          <stop offset="100%" stopColor="#78350f" />
                        </linearGradient>
                        <linearGradient id="giftRibbon" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fbbf24" />
                          <stop offset="100%" stopColor="#d97706" />
                        </linearGradient>
                      </defs>
                      <rect x="8" y="24" width="48" height="32" rx="2" fill="url(#giftBox)" />
                      <rect x="8" y="22" width="48" height="8" rx="2" fill="#92400e" />
                      <rect x="28" y="22" width="8" height="34" fill="url(#giftRibbon)" />
                      <path d="M32 22 C 22 14, 14 16, 18 20 C 22 24, 28 22, 32 22 Z" fill="url(#giftRibbon)" />
                      <path d="M32 22 C 42 14, 50 16, 46 20 C 42 24, 36 22, 32 22 Z" fill="url(#giftRibbon)" />
                      <circle cx="32" cy="22" r="3" fill="#fbbf24" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-amber-900 flex items-center gap-2">
                      Mystery Wellness Gift
                      <span className="text-[10px] font-bold bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">FREE</span>
                    </p>
                    <p className="text-sm text-amber-700">A little thank-you, included with orders over $300</p>
                    <p className="text-tea-700 font-bold mt-0.5">$0.00</p>
                  </div>
                  <p className="font-bold w-20 text-right text-tea-700">FREE</p>
                  <div className="w-6 ml-2" />
                </div>
              )}
            </div>

            {/* Order summary */}
            <div className="card h-fit">
              <h3 className="font-bold text-lg mb-4">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal {isMember && <span className="text-[10px] font-bold uppercase text-amber-600">VIP</span>}</span>
                  <span>{fmt(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>
                    {shipping === 0
                      ? <span className="text-tea-600 font-medium">FREE</span>
                      : fmt(shipping)}
                  </span>
                </div>
                {shipping > 0 && (
                  <p className="text-xs text-gray-500">Free shipping on orders over {fmt(20000)}</p>
                )}
                <p className="text-xs text-gray-500">Shipping outside the US adds a flat {fmt(5000)} surcharge at checkout.</p>
                <div className="border-t pt-2 flex justify-between font-bold text-base">
                  <span>Total</span>
                  <span>{fmt(subtotal + shipping)}</span>
                </div>
              </div>

              {/* Non-member upsell: how much VIP would save on THIS cart */}
              {!isMember && vipSavings > 0 && (
                <Link to="/membership" className="mt-4 block rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 hover:border-amber-300 transition-colors">
                  <p className="text-sm font-bold text-amber-800">
                    ★ Save {fmt(vipSavings)} with VIP
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Plus free shipping on this order. Join from $5/mo →
                  </p>
                </Link>
              )}

              <Link to="/checkout" className="btn-primary w-full mt-6 text-center block">
                Proceed to Checkout
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
